import React from 'react';
import { Heart, UserCheck, Check } from 'lucide-react';

interface SignupOptionsFieldProps {
  allowVolunteers: boolean;
  allowAttendees: boolean;
  onChange: (next: { allow_volunteers: boolean; allow_attendees: boolean }) => void;
}

const SignupOptionsField: React.FC<SignupOptionsFieldProps> = ({ allowVolunteers, allowAttendees, onChange }) => {
  const options = [
    {
      key: 'allow_attendees' as const,
      checked: allowAttendees,
      label: 'Attendees',
      hint: 'Shows an Attend button so people can RSVP',
      icon: UserCheck,
      active: 'border-emerald-500 bg-emerald-50',
      iconColor: 'text-emerald-600',
      box: 'bg-emerald-600 border-emerald-600',
    },
    {
      key: 'allow_volunteers' as const,
      checked: allowVolunteers,
      label: 'Volunteers',
      hint: 'Shows a Volunteer button so people can help out',
      icon: Heart,
      active: 'border-red-500 bg-red-50',
      iconColor: 'text-red-600',
      box: 'bg-red-600 border-red-600',
    },
  ];

  return (
    <fieldset>
      <legend className="block text-sm font-medium text-gray-700 mb-1">Sign-ups</legend>
      <p className="text-xs text-gray-500 mb-3">Choose what people can sign up for. You can pick both, or neither.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map(({ key, checked, label, hint, icon: Icon, active, iconColor, box }) => (
          <label
            key={key}
            className={`relative flex items-start gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 hover:shadow-sm ${
              checked ? active : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              onChange={(e) =>
                onChange({
                  allow_volunteers: allowVolunteers,
                  allow_attendees: allowAttendees,
                  [key]: e.target.checked,
                })
              }
            />
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
                checked ? box : 'border-gray-300 bg-white'
              }`}
            >
              {checked && <Check className="h-3.5 w-3.5 text-white" />}
            </span>
            <span className="flex-1">
              <span className="flex items-center gap-1.5 font-medium text-gray-900">
                <Icon className={`h-4 w-4 ${iconColor}`} />
                {label}
              </span>
              <span className="block text-xs text-gray-600 mt-1">{hint}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export default SignupOptionsField;
